<template>
  <div class="customers">
    <PageHeader title="Customers" :action="{ title: 'Add Customer', to: { name: 'CreateCustomer' } }" />

    <BaseTable
      :items="customers"
      :is-loading="isPending"
      :paginate="true"
      :error="error"
      :page="currentPage"
      :total-pages="totalPages"
      :total-records="totalRecords"
      :number-per-page="numPerPage"
      :fields="fields"
      :is-searchable="true"
      :filter-items="filterItems"
      :clickable="true"
      :next-page="nextPage"
      :prev-page="prevPage"
      @on-search="(val) => search(val)"
      @row-selected="rowSelected"
    >
      <template #isCompany-data="{ row }">
        <div class="ml-4 flex items-center justify-center w-10 h-10 rounded bg-slate-100 ring-1 ring-slate-300">
          <span v-if="row.isCompany" class="i-mdi-building size-6 text-accent" />
          <span v-else class="i-mdi-user-outline w-6 h-6 text-accent" />
        </div>
      </template>

      <template #name-data="{ row }">
        <span class="font-semibold text-slate-950">{{ row.name }}</span>
      </template>

      <template #contact-data="{ row }">
        <div class="flex flex-col sm:flex-row gap-4">
          <span v-if="row.email" class="flex gap-2 items-center">
            <span class="i-mdi-email size-4 text-slate-500" />
            <span class="text-sm text-slate-500">{{ row.email }}</span>
          </span>
          <span v-if="row.phone" class="flex gap-2 items-center">
            <span class="i-mdi-phone size-4 text-slate-500" />
            <span class="text-sm text-slate-500">{{ row.phone }}</span>
          </span>
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/app/AppPageHeader.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import { Customer } from '@/models/customer'
import CustomerService from '@/services/CustomerService'
import { where } from 'firebase/firestore'
import { useSearchablePagination } from '@/composables/useSearchablePagination'

useHead({ title: 'Customers' })

const numPerPage = ref(25)

const {
  results: customers,
  isPending,
  error,
  search,
  currentPage,
  totalPages,
  totalRecords,
  nextPage,
  prevPage,
} = useSearchablePagination<Customer>({
  indexName: 'customers',
  service: new CustomerService(),
  numPerPage: numPerPage.value,
  defaultOrderBy: 'name',
  defaultOrderDir: 'asc'
})

const fields = [
  { key: 'isCompany', label: 'Type', sortable: true },
  { key: 'name', label: 'Customer', sortable: true },
  { key: 'contact', label: 'Contact' },
]

const filterItems = [
  { label: 'All', value: null },
  { label: 'Company', value: where('isCompany', '==', true) },
  { label: 'Customer', value: where('isCompany', '==', false) },
]

const $router = useRouter()
const rowSelected = (row: Customer) => {
  $router.push({ name: 'ShowCustomer', params: { id: row.id } })
}
</script>
